import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { addDays } from "date-fns";
import { api } from "../lib/api";
import { ymd } from "../lib/date";
import { useEffectiveUser } from "../lib/auth";
import { can } from "../lib/permissions";
import type {
  Department, Employee, ScenarioAssignment, ScenarioTimeOff,
  ScheduleScenario, Shift,
} from "../lib/types";
import { Button } from "../components/ui/Button";
import { Field, Input, Select, Textarea } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";

// -----------------------------------------------------------------
// Mock schedules (schedule_scenarios) — index page.
//
// Lists every scenario the signed-in user can see (L2 = own dept
// only, L1+ = all) with Create / Open / Publish / Delete controls.
// A scenario is a draft schedule that doesn't touch anything live
// until it's published.
// -----------------------------------------------------------------
export function MockSchedulesIndex() {
  const me = useEffectiveUser();
  if (!can.seeMockSchedules(me)) return <Navigate to="/" replace />;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const scenarios = useQuery({ queryKey: ["scenarios"], queryFn: () => api.listScenarios() });

  const [showArchived, setShowArchived] = useState(false);
  const archived = useQuery({
    queryKey: ["scenarios", "archived"],
    queryFn: () => api.listScenarios({ includeArchived: true }),
    enabled: showArchived,
  });

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteScenario(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scenarios"] });
      setArmedDeleteId(null);
      setError(null);
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Delete failed."),
  });

  // L2 can only create for their own dept, so scope the create
  // picker to that dept — same list the backend will enforce.
  const myEmp = useMemo(() => {
    if (!me?.employeeId) return null;
    return (employees.data ?? []).find((e) => e.id === me.employeeId) ?? null;
  }, [me?.employeeId, employees.data]);
  const createableDepts = useMemo(() => {
    const all = (departments.data ?? []).slice().sort(
      (a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999)
        || a.name.localeCompare(b.name),
    );
    if (me?.level === "owner" || me?.level === "l1") return all;
    if (me?.level === "l2" && myEmp?.departmentId) {
      return all.filter((d) => d.id === myEmp.departmentId);
    }
    return [] as Department[];
  }, [departments.data, me?.level, myEmp?.departmentId]);
  const deptById = useMemo(
    () => new Map((departments.data ?? []).map((d) => [d.id, d])),
    [departments.data],
  );

  const visibleScenarios = (scenarios.data ?? []).slice()
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Mock schedules</h1>
          <p className="text-sm text-slate-500">
            Draft "what-if" schedules. Edit freely — nothing touches
            the live schedule, dashboard, coverage math, records, or
            notifications until you publish. L2 sees their own dept
            only; L1 and up see every department.
          </p>
        </div>
        {createableDepts.length > 0 && (
          <Button onClick={() => { setError(null); setCreating(true); }}>
            + New mock schedule
          </Button>
        )}
      </header>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {scenarios.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : visibleScenarios.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-6 text-center text-sm text-slate-500">
          No mock schedules yet. Click <strong>+ New mock schedule</strong> to draft one.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 font-semibold">Name</th>
                <th className="px-4 py-2 font-semibold">Department</th>
                <th className="px-4 py-2 font-semibold">Window</th>
                <th className="px-4 py-2 font-semibold">Created</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visibleScenarios.map((s) => {
                const dept = deptById.get(s.departmentId);
                return (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium text-slate-800">
                      <Link to={`/mock-schedules/${s.id}`} className="hover:underline">
                        {s.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-slate-600">{dept?.name ?? "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-600">
                      {s.startDate} → {s.endDate}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">
                      {new Date(s.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      <Link
                        to={`/mock-schedules/${s.id}`}
                        className="text-xs font-semibold text-sky-700 hover:underline mr-3"
                      >
                        Open →
                      </Link>
                      {armedDeleteId === s.id ? (
                        <button
                          type="button"
                          className="text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded px-2 py-0.5"
                          onClick={() => deleteMut.mutate(s.id)}
                          disabled={deleteMut.isPending}
                        >
                          Confirm delete
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setArmedDeleteId(s.id)}
                          className="text-xs font-semibold text-red-700 hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          className="text-xs text-slate-500 hover:text-slate-800 font-medium"
        >
          {showArchived ? "▾" : "▸"} Published / archived ({(archived.data ?? []).filter((s) => s.publishedAt).length})
        </button>
        {showArchived && (
          <div className="mt-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 font-semibold">Name</th>
                  <th className="px-4 py-2 font-semibold">Department</th>
                  <th className="px-4 py-2 font-semibold">Window</th>
                  <th className="px-4 py-2 font-semibold">Published</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(archived.data ?? [])
                  .filter((s) => s.publishedAt)
                  .map((s) => {
                    const dept = deptById.get(s.departmentId);
                    return (
                      <tr key={s.id} className="text-slate-600">
                        <td className="px-4 py-2">{s.name}</td>
                        <td className="px-4 py-2">{dept?.name ?? "—"}</td>
                        <td className="px-4 py-2 font-mono text-xs">{s.startDate} → {s.endDate}</td>
                        <td className="px-4 py-2 text-xs">
                          {s.publishedAt
                            ? new Date(s.publishedAt).toLocaleString()
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                {(archived.data ?? []).filter((s) => s.publishedAt).length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-xs text-slate-400 italic">
                      Nothing published yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && (
        <CreateScenarioModal
          departments={createableDepts}
          onClose={() => setCreating(false)}
          onCreated={(s) => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ["scenarios"] });
            // Jump straight into the scenario's live Schedule view
            // (the mock editor IS the Schedule page in scenario
            // mode). Using in-SPA navigation keeps React Query
            // caches warm and avoids a full page reload, which was
            // tripping the beta-gate race during the reload and
            // bouncing the user to the dashboard.
            navigate(`/schedule/${s.departmentId}?scenario=${s.id}`);
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Create modal: name, department (locked to home dept for L2),
// start date (defaults to the Sunday on or before today), and the
// seed-from-live checkbox (on by default so the admin starts from
// what's actually scheduled).
function CreateScenarioModal({
  departments, onClose, onCreated,
}: {
  departments: Department[];
  onClose: () => void;
  onCreated: (s: ScheduleScenario) => void;
}) {
  function sundayOf(d: Date): Date {
    const c = new Date(d); c.setHours(0, 0, 0, 0);
    c.setDate(c.getDate() - c.getDay());
    return c;
  }
  const [name, setName] = useState("");
  const [departmentId, setDepartmentId] = useState(departments[0]?.id ?? "");
  const [startDate, setStartDate] = useState(ymd(sundayOf(new Date())));
  const [seedFromLive, setSeedFromLive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Window = 28 days (4 weeks) — matches the live Schedule's
  // natural pay-period layout. This is also the hard cap; there's
  // no extend button on the editor any more.
  const defaultEndDate = useMemo(() => {
    try {
      const d = new Date(startDate + "T00:00:00");
      d.setDate(d.getDate() + 27);
      return ymd(d);
    } catch { return startDate; }
  }, [startDate]);
  const endDate = defaultEndDate;

  const createMut = useMutation({
    mutationFn: () => api.createScenario({
      departmentId, name, startDate, endDate, seedFromLive,
    }),
    onSuccess: (s) => onCreated(s),
    onError: (e: { message?: string }) => setError(e?.message ?? "Couldn't create."),
  });

  return (
    <Modal
      open={true}
      onClose={() => { if (!createMut.isPending) onClose(); }}
      title="New mock schedule"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={createMut.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => { setError(null); createMut.mutate(); }}
            disabled={createMut.isPending || !name.trim() || !departmentId || !startDate || !endDate}
          >
            {createMut.isPending ? "Creating…" : "Create & open"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. After Smith resigns"
          />
        </Field>
        <Field label="Department">
          <Select
            value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)}
            disabled={departments.length === 1}
          >
            {departments.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </Select>
        </Field>
        <Field label="Start date">
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <p className="mt-1 text-[11px] text-slate-500">
            Window is fixed at 4 weeks ({startDate} → {endDate}).
          </p>
        </Field>
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4"
            checked={seedFromLive}
            onChange={(e) => setSeedFromLive(e.target.checked)}
          />
          <span>
            <span className="font-medium">Seed days-off patterns only</span>
            <span className="block text-xs text-slate-500">
              Carries over each officer's XXX days-off pattern. Codes
              (V, S, TR, custom hours, OT) are NOT copied — the scenario
              starts clean so you only see what you add. Leave off to
              start blank.
            </span>
          </span>
        </label>
        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------
// Editor page — in-place redirect to the live Schedule page in
// scenario mode. The actual UI is the full Schedule grid rendered
// against the scenario shadow tables, so a mock schedule looks and
// behaves exactly like the real one (shift bars, OIC / FTO badges,
// extended-range chips, drag-and-drop, everything). The Schedule
// page reads ?scenario=<id> from the URL to flip into scenario
// mode and show the red banner.
export function MockScheduleEditor() {
  const me = useEffectiveUser();
  if (!can.seeMockSchedules(me)) return <Navigate to="/" replace />;
  const { id } = useParams<{ id: string }>();
  const scenarioId = id ?? "";
  const scenarioQ = useQuery({
    queryKey: ["scenario", scenarioId],
    queryFn: () => api.getScenario(scenarioId),
    enabled: !!scenarioId,
  });
  const scenario = scenarioQ.data ?? null;
  if (scenarioQ.isLoading) {
    return <div className="p-6 text-sm text-slate-500">Loading mock schedule…</div>;
  }
  if (!scenario) {
    return <Navigate to="/mock-schedules" replace />;
  }
  return (
    <Navigate
      to={`/schedule/${scenario.departmentId}?scenario=${scenario.id}`}
      replace
    />
  );
}

// ---------------------------------------------------------------
// Legacy standalone editor (now unused — the editor route redirects
// to the live Schedule page in scenario mode). Kept exported under
// a different name in case we want a simplified read-only diff view
// later. Not reachable from any current route.
export function MockScheduleEditorLegacy() {
  const me = useEffectiveUser();
  if (!can.seeMockSchedules(me)) return <Navigate to="/" replace />;
  const { id } = useParams<{ id: string }>();
  const scenarioId = id ?? "";
  const qc = useQueryClient();
  const navigate = useNavigate();

  const scenarioQ = useQuery({
    queryKey: ["scenario", scenarioId],
    queryFn: () => api.getScenario(scenarioId),
    enabled: !!scenarioId,
  });
  const assignmentsQ = useQuery({
    queryKey: ["scenarioAssignments", scenarioId],
    queryFn: () => api.listScenarioAssignments(scenarioId),
    enabled: !!scenarioId,
  });
  const timeOffQ = useQuery({
    queryKey: ["scenarioTimeOff", scenarioId],
    queryFn: () => api.listScenarioTimeOff(scenarioId),
    enabled: !!scenarioId,
  });
  const excludedQ = useQuery({
    queryKey: ["scenarioExcluded", scenarioId],
    queryFn: () => api.listScenarioExcluded(scenarioId),
    enabled: !!scenarioId,
  });
  const employeesQ = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const shiftsQ = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const codesQ = useQuery({ queryKey: ["shiftCodes"], queryFn: api.listShiftCodes });
  const departmentsQ = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });

  const [error, setError] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [vacationOpen, setVacationOpen] = useState(false);
  const [cellPicker, setCellPicker] = useState<null | {
    employeeId: string; date: string; existing: ScenarioAssignment | null;
  }>(null);
  // Two-click confirm for the officer × "exclude" button. First
  // click arms the row (chip reads "Confirm remove"); second click
  // actually fires the mutation. Reset on blur / another officer
  // click so an abandoned confirm doesn't linger.
  const [armedExcludeId, setArmedExcludeId] = useState<string | null>(null);

  const scenario = scenarioQ.data;
  const dept = useMemo(() => (
    scenario
      ? (departmentsQ.data ?? []).find((d) => d.id === scenario.departmentId) ?? null
      : null
  ), [scenario, departmentsQ.data]);

  const windowDays = useMemo(() => {
    if (!scenario) return [] as Date[];
    const start = new Date(scenario.startDate + "T00:00:00");
    const end = new Date(scenario.endDate + "T00:00:00");
    const out: Date[] = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) out.push(new Date(d));
    return out;
  }, [scenario]);

  const excludedSet = useMemo(() => new Set(excludedQ.data ?? []), [excludedQ.data]);
  const deptOfficers = useMemo(() => {
    if (!scenario) return [] as Employee[];
    return (employeesQ.data ?? [])
      .filter((e) => e.departmentId === scenario.departmentId)
      .filter((e) => e.status !== "inactive" && !e.deletedAt)
      .sort((a, b) =>
        a.lastName.localeCompare(b.lastName)
        || a.firstName.localeCompare(b.firstName),
      );
  }, [scenario, employeesQ.data]);
  const activeOfficers = deptOfficers.filter((e) => !excludedSet.has(e.id));

  // (employeeId|date) → scenario_assignment row for the grid cell.
  const assignByKey = useMemo(() => {
    const m = new Map<string, ScenarioAssignment>();
    for (const a of assignmentsQ.data ?? []) m.set(`${a.employeeId}|${a.date}`, a);
    return m;
  }, [assignmentsQ.data]);

  // (employeeId|date) → first time-off row covering that cell.
  const timeOffByKey = useMemo(() => {
    const m = new Map<string, ScenarioTimeOff>();
    for (const t of timeOffQ.data ?? []) {
      const start = new Date(t.startDate + "T00:00:00");
      const end = new Date(t.endDate + "T00:00:00");
      for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
        m.set(`${t.employeeId}|${ymd(d)}`, t);
      }
    }
    return m;
  }, [timeOffQ.data]);

  const shiftById = useMemo(
    () => new Map((shiftsQ.data ?? []).map((s) => [s.id, s])),
    [shiftsQ.data],
  );
  function homeShiftFor(emp: Employee): Shift | null {
    if (!emp.homeShiftId) return null;
    return shiftById.get(emp.homeShiftId) ?? null;
  }

  const publishMut = useMutation({
    mutationFn: () => api.publishScenario(scenarioId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scenarios"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      qc.invalidateQueries({ queryKey: ["timeoff"] });
      navigate("/mock-schedules");
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Publish failed."),
  });
  const extendMut = useMutation({
    mutationFn: async () => {
      if (!scenario) return;
      const next = ymd(addDays(new Date(scenario.endDate + "T00:00:00"), 14));
      return api.updateScenario(scenario.id, { endDate: next });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scenario", scenarioId] }),
    onError: (e: { message?: string }) => setError(e?.message ?? "Extend failed."),
  });
  const excludeMut = useMutation({
    mutationFn: (empId: string) => api.excludeScenarioEmployee(scenarioId, empId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scenarioExcluded", scenarioId] }),
  });
  const includeMut = useMutation({
    mutationFn: (empId: string) => api.includeScenarioEmployee(scenarioId, empId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scenarioExcluded", scenarioId] }),
  });

  if (!scenario && !scenarioQ.isLoading) {
    return <Navigate to="/mock-schedules" replace />;
  }
  if (!scenario) {
    return <div className="p-6 text-sm text-slate-500">Loading mock schedule…</div>;
  }
  const isPublished = !!scenario.publishedAt;

  return (
    <div className="flex flex-col h-full">
      {/* Red "this is a mock" banner — carries the Publish /
        * Extend-window / Back actions. Stays visible while editing
        * so there's never any doubt the admin is working against a
        * draft and not the live schedule. */}
      <div className="shrink-0 border-b border-red-400/60 bg-red-600 text-white print:hidden">
        <div className="px-3 py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className="hidden sm:inline uppercase text-[10px] md:text-xs bg-white/15 border border-white/20 rounded px-1.5 py-0.5 font-semibold shrink-0">
              Mock schedule
            </span>
            <div className="min-w-0">
              <div className="text-sm md:text-base font-semibold truncate">
                {scenario.name}
                <span className="ml-2 font-normal opacity-80 text-xs md:text-sm">
                  · {dept?.name ?? "—"} · {scenario.startDate} → {scenario.endDate}
                </span>
              </div>
              <div className="text-[11px] opacity-80 hidden md:block">
                Nothing here touches the live schedule, dashboard, coverage, or notifications. Publish to replace the live schedule for this window.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/mock-schedules"
              className="text-xs font-medium underline-offset-2 hover:underline"
            >
              ← Back
            </Link>
            {!isPublished && (
              <>
                <button
                  type="button"
                  onClick={() => extendMut.mutate()}
                  disabled={extendMut.isPending}
                  className="rounded bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-2 py-1 border border-white/20"
                  title="Add two more weeks to the end of the window"
                >
                  + 2 wks
                </button>
                <button
                  type="button"
                  onClick={() => setVacationOpen(true)}
                  className="rounded bg-white/20 hover:bg-white/30 text-white text-xs font-semibold px-2 py-1 border border-white/20"
                >
                  + Vacation
                </button>
                <button
                  type="button"
                  onClick={() => { setError(null); setPublishOpen(true); }}
                  className="rounded bg-white text-red-700 hover:bg-red-50 text-xs font-semibold px-3 py-1 shadow-sm"
                >
                  Publish →
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-4">
        {error && (
          <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="text-xs" style={{ tableLayout: "fixed", width: "auto" }}>
              <colgroup>
                <col style={{ width: 180 }} />
                {windowDays.map((d) => (
                  <col key={d.toISOString()} style={{ width: 48 }} />
                ))}
              </colgroup>
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500">
                  <th className="text-left px-3 py-2 sticky left-0 bg-slate-50 font-semibold">
                    Officer
                  </th>
                  {windowDays.map((d) => (
                    <th
                      key={d.toISOString()}
                      className={
                        "text-center px-0 py-2 font-semibold "
                        + (d.getDay() === 0 || d.getDay() === 6 ? "bg-slate-100" : "")
                      }
                      title={ymd(d)}
                    >
                      <div className="text-[9px] uppercase tracking-wider text-slate-400">
                        {d.toLocaleDateString(undefined, { weekday: "narrow" })}
                      </div>
                      <div>{d.getDate()}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeOfficers.map((emp) => {
                  const home = homeShiftFor(emp);
                  return (
                    <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-1 sticky left-0 bg-white truncate">
                        <div className="flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-slate-800 truncate">
                              {emp.lastName}, {emp.firstName}
                            </div>
                            {home && (
                              <div className="text-[10px] text-slate-400 truncate">
                                {home.name} · {home.startTime}
                              </div>
                            )}
                          </div>
                          {!isPublished && (
                            armedExcludeId === emp.id ? (
                              <button
                                type="button"
                                onClick={() => {
                                  excludeMut.mutate(emp.id);
                                  setArmedExcludeId(null);
                                }}
                                onBlur={() => setArmedExcludeId(null)}
                                autoFocus
                                className="text-[10px] font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded px-1.5 py-0.5 uppercase tracking-wider"
                                title="Click again to remove"
                              >
                                Confirm
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setArmedExcludeId(emp.id)}
                                className="text-slate-300 hover:text-rose-600 text-xs leading-none px-1"
                                title={`Remove ${emp.firstName} ${emp.lastName} from this scenario — simulates a departure`}
                                aria-label="Remove from scenario"
                              >
                                ×
                              </button>
                            )
                          )}
                        </div>
                      </td>
                      {windowDays.map((d) => {
                        const date = ymd(d);
                        const key = `${emp.id}|${date}`;
                        const a = assignByKey.get(key);
                        const to = timeOffByKey.get(key);
                        const label = (a?.note ?? "").trim()
                          || (to ? "V" : "")
                          || "";
                        const weekend = d.getDay() === 0 || d.getDay() === 6;
                        return (
                          <td
                            key={date}
                            onDoubleClick={isPublished
                              ? undefined
                              : () => setCellPicker({
                                  employeeId: emp.id,
                                  date,
                                  existing: a ?? null,
                                })}
                            className={
                              "text-center text-[11px] py-2 tabular-nums font-semibold "
                              + (weekend ? "bg-slate-50/60 " : "")
                              + (label ? "text-slate-800 " : "text-transparent ")
                              + (isPublished ? "" : "cursor-pointer hover:bg-indigo-50/60")
                            }
                            title={isPublished
                              ? "Published — read-only."
                              : "Double-click to set a code"}
                          >
                            {label || "·"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {activeOfficers.length === 0 && (
                  <tr>
                    <td colSpan={1 + windowDays.length} className="p-6 text-center text-slate-500 text-sm">
                      No officers in this department yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {excludedSet.size > 0 && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
            <div className="font-semibold text-xs uppercase tracking-wider mb-1">
              Excluded from this scenario ({excludedSet.size})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {deptOfficers.filter((e) => excludedSet.has(e.id)).map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => includeMut.mutate(e.id)}
                  disabled={isPublished}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-300 bg-white text-amber-900 hover:bg-amber-100 text-xs font-medium disabled:opacity-50"
                  title="Bring this officer back into the scenario"
                >
                  {e.lastName}, {e.firstName} <span className="text-amber-600">↩</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {cellPicker && (
        <CellPicker
          scenarioId={scenarioId}
          employeeId={cellPicker.employeeId}
          date={cellPicker.date}
          existing={cellPicker.existing}
          homeShift={homeShiftFor(deptOfficers.find((e) => e.id === cellPicker.employeeId)!)}
          codes={(codesQ.data ?? []).map((c) => c.code)}
          onClose={() => setCellPicker(null)}
        />
      )}

      {vacationOpen && (
        <VacationModal
          scenarioId={scenarioId}
          officers={activeOfficers}
          windowStart={scenario.startDate}
          windowEnd={scenario.endDate}
          onClose={() => setVacationOpen(false)}
        />
      )}

      {publishOpen && (
        <PublishModal
          scenario={scenario}
          dept={dept}
          assignmentsCount={(assignmentsQ.data ?? []).length}
          timeOffCount={(timeOffQ.data ?? []).length}
          excludedCount={excludedSet.size}
          onClose={() => setPublishOpen(false)}
          onConfirm={() => publishMut.mutate()}
          busy={publishMut.isPending}
        />
      )}
    </div>
  );
}

function CellPicker({
  scenarioId, employeeId, date, existing, homeShift, codes, onClose,
}: {
  scenarioId: string;
  employeeId: string;
  date: string;
  existing: ScenarioAssignment | null;
  homeShift: Shift | null;
  codes: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [note, setNote] = useState(existing?.note ?? "");
  const [error, setError] = useState<string | null>(null);
  // Armed-confirm for the destructive "Clear cell" button — one
  // click arms it, second click actually clears. Resets on blur
  // or when the user changes the draft note.
  const [armedClear, setArmedClear] = useState(false);
  const presets = ["XXX", "V", "S", "C", "PL", "TR", "OT"];

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["scenarioAssignments", scenarioId] });
  }
  const saveMut = useMutation({
    mutationFn: async (noteVal: string | null) => {
      if (noteVal === null || noteVal.trim() === "") {
        await api.clearScenarioCell({ scenarioId, employeeId, date });
      } else {
        if (!homeShift) {
          throw new Error("This officer has no home shift set — set one on the Employees page before scheduling them in a scenario.");
        }
        await api.upsertScenarioAssignment({
          scenarioId, employeeId, date,
          shiftId: homeShift.id,
          note: noteVal.trim(),
        });
      }
    },
    onSuccess: () => { invalidate(); onClose(); },
    onError: (e: { message?: string }) => setError(e?.message ?? "Save failed."),
  });

  return (
    <Modal
      open={true}
      onClose={() => { if (!saveMut.isPending) onClose(); }}
      title={`Cell · ${date}`}
      footer={
        <>
          <Button
            variant="danger"
            onClick={() => {
              if (armedClear) {
                saveMut.mutate(null);
                setArmedClear(false);
              } else {
                setArmedClear(true);
              }
            }}
            onBlur={() => setArmedClear(false)}
            disabled={saveMut.isPending}
            className="mr-auto"
            title={armedClear
              ? "Click again to clear this cell"
              : "Clear the scenario cell — the officer will show as working / unassigned"}
          >
            {armedClear ? "Confirm clear" : "Clear cell"}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={saveMut.isPending}>
            Cancel
          </Button>
          <Button onClick={() => saveMut.mutate(note)} disabled={saveMut.isPending}>
            {saveMut.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="text-xs text-slate-500">
          {homeShift
            ? <>Shift: <strong className="text-slate-800">{homeShift.name}</strong> · {homeShift.startTime}</>
            : "No home shift set for this officer."}
        </div>
        <Field label="Code / note">
          <Input
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. V, XXX, 09-15"
          />
        </Field>
        <div className="flex flex-wrap gap-1.5">
          {Array.from(new Set([...presets, ...codes])).map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setNote(c)}
              className={
                "px-2 py-0.5 rounded border text-xs font-semibold "
                + (note === c
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")
              }
            >
              {c}
            </button>
          ))}
        </div>
        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

function VacationModal({
  scenarioId, officers, windowStart, windowEnd, onClose,
}: {
  scenarioId: string;
  officers: Employee[];
  windowStart: string;
  windowEnd: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [employeeId, setEmployeeId] = useState(officers[0]?.id ?? "");
  const [startDate, setStartDate] = useState(windowStart);
  const [endDate, setEndDate] = useState(windowStart);
  const [type, setType] = useState("vacation");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => api.createScenarioTimeOff({
      scenarioId, employeeId,
      startDate, endDate,
      type, note: note.trim() || null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scenarioTimeOff", scenarioId] });
      onClose();
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Save failed."),
  });

  return (
    <Modal
      open={true}
      onClose={() => { if (!mut.isPending) onClose(); }}
      title="Add time off (scenario)"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mut.isPending}>Cancel</Button>
          <Button
            onClick={() => { setError(null); mut.mutate(); }}
            disabled={mut.isPending || !employeeId || !startDate || !endDate}
          >
            {mut.isPending ? "Saving…" : "Add"}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Officer">
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            {officers.map((e) => (
              <option key={e.id} value={e.id}>{e.lastName}, {e.firstName}</option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Start">
            <Input
              type="date"
              value={startDate}
              min={windowStart}
              max={windowEnd}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Field>
          <Field label="End">
            <Input
              type="date"
              value={endDate}
              min={startDate}
              max={windowEnd}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Field>
        </div>
        <Field label="Type">
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            <option value="vacation">Vacation</option>
            <option value="sick">Sick</option>
            <option value="comp">Comp</option>
            <option value="personal">Personal</option>
            <option value="other">Other</option>
          </Select>
        </Field>
        <Field label="Note (optional)">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
          />
        </Field>
        <p className="text-[11px] text-slate-500">
          Auto-approved inside the scenario — no request/review step.
          Shows on the mock grid now; copies to the live Time Off page
          on publish.
        </p>
        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

function PublishModal({
  scenario, dept, assignmentsCount, timeOffCount, excludedCount,
  onClose, onConfirm, busy,
}: {
  scenario: ScheduleScenario;
  dept: Department | null;
  assignmentsCount: number;
  timeOffCount: number;
  excludedCount: number;
  onClose: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  const [confirmText, setConfirmText] = useState("");
  const phrase = `publish ${scenario.name.toLowerCase()}`;
  const matches = confirmText.trim().toLowerCase() === phrase;
  return (
    <Modal
      open={true}
      onClose={() => { if (!busy) onClose(); }}
      title={`Publish "${scenario.name}"?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={!matches || busy}
          >
            {busy ? "Publishing…" : "Publish & replace live schedule"}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-900">
          Publishing <strong>replaces</strong> every live assignment
          for <strong>{dept?.name ?? "the department"}</strong> between{" "}
          <strong>{scenario.startDate}</strong> and{" "}
          <strong>{scenario.endDate}</strong>. This cannot be undone — the
          scenario is archived after publish.
        </div>
        <ul className="text-xs text-slate-700 space-y-0.5">
          <li>• {assignmentsCount} scenario assignment{assignmentsCount === 1 ? "" : "s"} land on the live schedule.</li>
          <li>• {timeOffCount} scenario time-off row{timeOffCount === 1 ? "" : "s"} added to live time-off.</li>
          <li>• {excludedCount} officer{excludedCount === 1 ? "" : "s"} excluded from the scenario — their live assignments in the window are removed.</li>
          <li>• Existing live time-off / training records are <strong>preserved</strong>.</li>
        </ul>
        <Field label={`Type "${phrase}" to confirm`}>
          <Input
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={phrase}
          />
        </Field>
      </div>
    </Modal>
  );
}
